"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports._safeSendBeacon = void 0;
/**
 * Send beacon data with navigator.sendBeacon() failing gracefully on errors.
 * Returns `true` if successful, `false` otherwise.
 *
 * @param url - The URL to send the beacon to
 * @param payload - The data to send with the beacon
 * @param debug - Global debug instance
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon
 * @private
 */
function _safeSendBeacon(url, payload, debug) {
    // Bail if we're not in a browser environment. See PERF-802.
    if (typeof window === 'undefined') {
        console.error('`window` must be defined.');
        return false;
    }
    if (typeof url !== 'string') {
        console.error('The beacon URL must be a string');
        return false;
    }
    // Send the beacon data. Catch data queue errors and other low-level
    // function errors. See:
    //  - https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon#return_values
    //  - https://xgwang.me/posts/you-may-not-know-beacon/#it-may-throw-error%2C-be-sure-to-catch
    try {
        const JSONPayload = JSON.stringify(payload);
        const queueSuccess = window.navigator.sendBeacon(url, JSONPayload);
        if (!queueSuccess)
            throw new Error('Data not queued for transfer');
    }
    catch (err) {
        console.error('Beacon failure!', err);
        console.error(`\tURL: ${url}`);
        console.error(`\tData:`, payload);
        return false;
    }
    debug.log('Data beacon success!', payload);
    return true;
}
exports._safeSendBeacon = _safeSendBeacon;
/**
 * Send the specified data to the specified beacon URL.
 *
 * @param opts - The beacon options
 * @param safeSendBeaconFn - Optional function override for testing
 *
 * @todo Modularize production domain check
 */
function sendBeacon(opts, safeSendBeaconFn = _safeSendBeacon) {
    const { beaconUrl, debug, eventsEnd = [], eventsStart = [], productionDomain, staticDimensions, trackedEventDetails = [], webVitalsDetails, } = opts;
    // Bail if we're not in a browser environment. See PERF-802.
    if (typeof window === 'undefined') {
        console.error('Beacon failure! `window` is undefined.');
        return false;
    }
    // Construct the beacon payload
    const beaconPayload = {
        eventsEnd,
        eventsStart,
        staticDimensions,
        trackedEventDetails,
    };
    // If `staticDimensions.environment` is set to "production" but the browser
    // location says otherwise, discard the beacon and log an error. Skip this
    // check if `productionDomain` is missing or falsy.
    // TODO: Centralize `window` checks
    if (productionDomain) {
        const { hostname } = window.location;
        const { environment } = beaconPayload.staticDimensions;
        const isEnvironmentProduction = environment === 'production';
        const isLocationProduction = String(hostname).includes(productionDomain);
        if (!hostname) {
            console.error(`Beacon failure! Browser location hostname not found. Discarding beacon.`);
            return false;
        }
        if (isEnvironmentProduction && !isLocationProduction) {
            console.error(`Beacon failure! Production domain "${productionDomain}" not found in browser location "${hostname}". Discarding beacon.`);
            return false;
        }
    }
    // Only include web vitals details if they exist, omit if non available
    if (webVitalsDetails)
        beaconPayload.webVitalsDetails = webVitalsDetails;
    // Fire the beacon, using fallbacks if necessary
    return safeSendBeaconFn(beaconUrl, beaconPayload, debug);
}
exports.default = sendBeacon;
